import { Plugin, Store } from "vuex";
import { LightstreamerClient, Subscription } from "lightstreamer-client-web";
import { TelemetryTileConfig, TelemetryTileConfigInterface } from "@/components/tiles/telemetry.types";
import { LayoutInterface, TileConfig, UpdateTileInterface } from "@/store/config.types";
import { CLEAR_DATA, UPDATE_DATA, UPDATE_STATUS } from "@/store/telemetry";
import { TelemetryEntry, TelemetryStatus } from "./telemetry.types";
import { Decoder } from "./telemetry.mappings";
import { deep_clone } from "@/global.functions";

const TelemetryStorePlugin: Plugin<any> = (store: Store<any>) => {
  let client = new LightstreamerClient("https://push.lightstreamer.com", "ISSLIVE");
  // let client = new LightstreamerClient('https://push1.jsc.nasa.gov', 'PROXYTELEMETRY');
  let timeSub = new Subscription('MERGE', 'TIME_000001', ['Value', 'TimeStamp', 'Status.Class']);
  let valueSub = new Subscription('MERGE', [] , ['TimeStamp', 'Value', 'Status', 'CalibratedData']);
  client.connect();

  client.subscribe(timeSub);
  client.addListener({
    onStatusChange: (status) => {
      let telemetryStatus: TelemetryStatus = store.getters["telemetry/getStatus"]();
      if (status.startsWith("CONNECTING") || status === "DISCONNECTED:TRYING-RECOVERY") {
        telemetryStatus.number = -2;
        telemetryStatus.text = "Socket connecting";
      } else if (status.startsWith("CONNECTED")) {
        telemetryStatus.number = -3;
        telemetryStatus.text = "Socket connected";
      } else if (status.startsWith("DISCONNECTED")) {
        telemetryStatus.number = -1;
        telemetryStatus.text = "Socket disconnected";
      }

      store.commit("telemetry/" + UPDATE_STATUS, telemetryStatus);
    },
    onServerError: (code, message) => {
      let telemetryStatus: TelemetryStatus = store.getters["telemetry/getStatus"]();
      telemetryStatus.number = -9;
      telemetryStatus.text = "Socket error"

      console.error(`Unknown telemetry-server error: ${message}, Error Code: ${code}`);

      store.commit("telemetry/" + UPDATE_STATUS, telemetryStatus);
    }
  })

  timeSub.addListener({
    onItemUpdate: function (update) {
      let telemetryStatus: TelemetryStatus = store.getters["telemetry/getStatus"]();
      let telemetryStatusValue: TelemetryEntry;
      let status = update.getValue("Status.Class");
      let timestamp = update.getValue("TimeStamp");

      telemetryStatusValue = Decoder.value(update.getItemName(), parseInt(status), update.getValue("TimeStamp"))[0];

      telemetryStatus.number = parseInt(status);
      telemetryStatus.text = status === '24' ? `Signal acquired` : `Signal lost`;
      telemetryStatus.time.last = Decoder.parseTimestamp(timestamp);

      let oldTelemetryStatusValue: TelemetryEntry = store.getters["telemetry/getValue"]("TIME_000001");
      if (oldTelemetryStatusValue && oldTelemetryStatusValue.value.value === telemetryStatusValue.value.value) {
        telemetryStatusValue.metadata.highlight = false;
      }
      store.commit("telemetry/" + UPDATE_DATA, [telemetryStatusValue]);
      store.commit("telemetry/" + UPDATE_STATUS, telemetryStatus);
    }
  });

  valueSub.addListener({
    onItemUpdate: (update) => {
      store.commit("telemetry/" + UPDATE_DATA, Decoder.value(update.getItemName(), parseFloat(update.getValue("Value")), update.getValue("TimeStamp")));
    }
  })

  setInterval(() => {
    let telemetryStatus: TelemetryStatus = store.getters["telemetry/getStatus"]();

    telemetryStatus.time.current = new Date();

    if (telemetryStatus.time.last) {
      telemetryStatus.time.delay = Math.abs(telemetryStatus.time.last.getTime() - telemetryStatus.time.current.getTime());
      telemetryStatus.time.delayCritical = telemetryStatus.time.delay > 30000;
    } else {
      telemetryStatus.time.delay = null;
      telemetryStatus.time.delayCritical = true;
    }

    store.commit("telemetry/" + UPDATE_STATUS, telemetryStatus);
  }, 500);

  function dataSubscribe(layout: LayoutInterface): void {
    let datapoints: Array<string> = [];

    layout.config.flat().filter(t => t.name === "telemetry")
      .forEach((tile) => {
        let config = tile.config as TelemetryTileConfigInterface;
        if (config.hasOwnProperty("datapoints")) {
          for (let dp of config.datapoints) {
            if (dp === "TIME_000001") continue;
            if (datapoints.indexOf(dp) < 0) datapoints.push(dp);
          }
        }
      })

    if (valueSub.isActive()) {
      store.commit("telemetry/" + CLEAR_DATA);
      client.unsubscribe(valueSub);
    }

    valueSub.setItems(datapoints);

    client.subscribe(valueSub);
  }
  
  store.watch(() => store.getters["config/getTileConfig"](), value => {
    if (value) {
      dataSubscribe(deep_clone(value));
    }
  }, {deep: true, immediate: true})
}

export default TelemetryStorePlugin;
